import { ExportArtifactKind, ImportRuleFileCategory } from '@/api/datastore/generated/model';

/**
 * Box 連携ルールのファイルカテゴリ表示順序。
 * UI のチップ表示や選択肢は、この順序に従って並べます（画像 → 動画 → CSV）。
 */
export const FILE_CATEGORY_DISPLAY_ORDER: readonly ImportRuleFileCategory[] = Object.freeze([
  ImportRuleFileCategory.image,
  ImportRuleFileCategory.video,
  ImportRuleFileCategory.csv,
]);

/**
 * Box 書込ルールの成果物種別（何を書き込むか）の表示順序。
 * UI の選択肢や詳細表示は、この順序に従って並べます（処理結果 → ブックマーク）。
 */
export const EXPORT_ARTIFACT_KIND_DISPLAY_ORDER: readonly ExportArtifactKind[] = Object.freeze([
  ExportArtifactKind.processing_result,
  ExportArtifactKind.bookmark,
]);

const sortByDisplayOrder = <T>(values: readonly T[], order: readonly T[]): T[] => {
  const rankOf = (value: T): number => {
    const index = order.indexOf(value);
    return index === -1 ? order.length : index;
  };
  return values.toSorted((a, b) => rankOf(a) - rankOf(b));
};

/** ファイルカテゴリを表示順に正規化します。 */
export const sortFileCategories = (
  categories: readonly ImportRuleFileCategory[],
): ImportRuleFileCategory[] => sortByDisplayOrder(categories, FILE_CATEGORY_DISPLAY_ORDER);

/** 成果物種別を表示順に正規化します。 */
export const sortArtifactKinds = (kinds: readonly ExportArtifactKind[]): ExportArtifactKind[] =>
  sortByDisplayOrder(kinds, EXPORT_ARTIFACT_KIND_DISPLAY_ORDER);

// ================================================================
// Box 取込ルールの入力項目の上限・下限
// サーバ側スキーマ (schemas/requests/create-box-import-rule-request.yaml) と DB スキーマに揃えます。
// ================================================================

/** ルール名の最大文字数（スキーマ: name.maxLength）。 */
export const IMPORT_RULE_NAME_MAX_LENGTH = 200;

/** メモの最大文字数（スキーマ: memo.maxLength）。 */
export const IMPORT_RULE_MEMO_MAX_LENGTH = 1000;

/** 取込対象の Box フォルダ URL の最小件数（スキーマ: target_folder_urls.minItems）。 */
export const IMPORT_RULE_TARGET_FOLDER_URLS_MIN_ITEMS = 1;

/**
 * 取込対象の Box フォルダ URL の最大件数。
 * OpenAPI スキーマには無いが、Laravel が `max:1000`（BoxImportRuleRequestRules::FOLDER_URL_MAX_ITEMS、
 * DoS 抑止）を強制しているのでその値のミラー。サーバが変えたら追随が必要。
 */
export const IMPORT_RULE_TARGET_FOLDER_URLS_MAX_ITEMS = 1000;

/** 取込対象の Box フォルダ URL 1 件あたりの最大文字数。DB カラム VARCHAR(762) に合わせた送信前ガード。 */
export const IMPORT_RULE_TARGET_FOLDER_URL_MAX_LENGTH = 762;

// ================================================================
// Box 書込ルールの入力項目の上限・下限
// 取込ルールの保存と同時に新規作成する書込ルール（schemas/requests/import-rule-new-export-rule.yaml）
// と、書込ルール単体の作成（schemas/requests/create-box-export-rule-request.yaml）で共通です。
// ================================================================

/** 書込ルール名の最大文字数（スキーマ: name.maxLength）。 */
export const EXPORT_RULE_NAME_MAX_LENGTH = 200;

/** メモの最大文字数（スキーマ: memo.maxLength）。 */
export const EXPORT_RULE_MEMO_MAX_LENGTH = 1000;

/** 書込先の Box フォルダ URL の最小件数（スキーマ: target_folder_urls.minItems）。 */
export const EXPORT_RULE_TARGET_FOLDER_URLS_MIN_ITEMS = 1;

/** 書込先の Box フォルダ URL の最大件数（スキーマ: target_folder_urls.maxItems）。 */
export const EXPORT_RULE_TARGET_FOLDER_URLS_MAX_ITEMS = 10;

/** 書込先の Box フォルダ URL 1 件あたりの最大文字数。DB カラム VARCHAR(762) に合わせた送信前ガード。 */
export const EXPORT_RULE_TARGET_FOLDER_URL_MAX_LENGTH = 762;

/** 自動書き込みの対象ワークフローの最大件数（スキーマ: target_workflow_ids.maxItems）。 */
export const EXPORT_RULE_TARGET_WORKFLOWS_MAX_ITEMS = 100;
